import { BannerType, useAppSelector } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { CmsBanner } from "@/components";
import { Col, SpicyButton } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";

import { buildChatBackGuardState, isChatBackGuardEntry } from "./chatBackGuard";

interface INavigateBackButtonProps {
  isChangePersona?: boolean;
  setIsChangePersona?: (value: boolean) => void;
  path?: string;
  onClick?: () => void;
}

const NavigateBackButton = ({ isChangePersona, setIsChangePersona, path, onClick }: INavigateBackButtonProps) => {
  const entryLocation = useAppSelector((state) => state.navigation.entryLocation);
  const { pagePath } = useLocalizedLocation();
  const navigate = useNavigate();
  const isChatPage = pagePath?.split("/")[1] === "chat";

  const handleBackClick = () => {
    if (isChangePersona) {
      setIsChangePersona?.(false);
    } else {
      if (path) {
        navigate(path);
      } else {
        navigate(-1);
      }
    }
  };

  const handleClick = onClick || handleBackClick;

  useEffect(() => {
    if (isChatPage && entryLocation === pagePath) {
      const handlePopState = () => {
        // Still on the guard entry: something above it was popped, not the chat itself.
        if (isChatBackGuardEntry(window.history.state)) {
          return;
        }
        navigate("/");
      };

      // Already on the guard, after a reload or a return to it: arm the listener only, or a second guard would
      // make the first Back do nothing.
      if (!isChatBackGuardEntry(window.history.state)) {
        window.history.pushState(buildChatBackGuardState(window.history.state), "", window.location.href);
      }

      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [entryLocation, pagePath, isChatPage, navigate]);

  return (
    <Col>
      <SpicyButton
        variant="tertiary_short"
        color="transparent"
        className="pl-0"
        onClick={handleClick}
        startContent={<Icon size={IconSize.MEDIUM} icon={IconVariants.CHEVRON_LEFT} />}
      >
        <span className="!text-black dark:!text-white text-base">
          <Trans i18nKey="common:back" />
        </span>
      </SpicyButton>
      <CmsBanner placement={BannerType.Top} shouldBeExcludedFromPagesWithTags={true} className={"mb-2"} isVisibleOnPartner={false} />
    </Col>
  );
};

export default NavigateBackButton;
